import { AlertTriangle } from 'lucide-react';

/**
 * Cuánto de esta tarea está hecho, declarado por quien la trabaja — **al lado de lo que se gastó**.
 *
 * **Por qué existe.** Hasta el 2026-10-05 el producto no podía expresar lo que Diego describió:
 * *«una tarea ya consumió el 100 % de las horas, pero está al 20 %»*. El Gantt la dibujaba al 100 %
 * —llamaba avance al consumo— y el avance del caso la contaba como 0 % —una tarea valía 0 o 100
 * según su estado—. Los dos números estaban mal en direcciones opuestas.
 *
 * **Por qué nunca se muestra solo.** Un porcentaje a mano se infla; es el síndrome del 90 %. Lo que
 * vale no es el número sino **la distancia** entre lo avanzado y lo gastado, así que el control va
 * siempre con el consumo al lado y con la alerta cuando la brecha se abre.
 *
 * **Por qué un desplegable y no un campo de texto.** Esto se usa en la bandeja, entre veinte tareas,
 * por alguien que está trabajando. Un campo que hay que tipear y guardar no se carga nunca, y un
 * indicador que nace vacío no sirve: declarar tiene que costar un clic.
 *
 * **Una tarea terminada no pregunta**: avanzó 100 y es un hecho del estado, no una opinión. Pedirle
 * a alguien que declare lo que ya está cerrado es ruido.
 */
export default function DeclararAvance({ task, onDeclarar, disabled = false, compacto = false }) {
  const horas = Number(task?.estimate_hours || 0);
  const consumidas = Number(task?.consumed_hours || 0);
  const declarado = task?.avance_declarado;
  const avance = task?.avance;
  const alerta = task?.consumio_mas_de_lo_que_avanzo;

  // El estado ya lo contestó: 100, y no se pregunta.
  const cerrada = !declarado && avance === 100;

  const consumoTexto = horas > 0
    ? `${consumidas} de ${horas} h`
    : (consumidas > 0 ? `${consumidas} h, sin estimar` : 'sin estimar');

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`avance-${task?.task_id}`}>
      {cerrada ? (
        <span className="text-[11px] text-zinc-500" title="El estado de la tarea ya dice que está hecha">
          avance 100 %
        </span>
      ) : (
        <label className="flex items-center gap-1 text-[11px] text-zinc-600">
          {!compacto && <span>avance</span>}
          <select
            className="border border-zinc-200 rounded-sm px-1 py-0.5 text-[11px] bg-white"
            value={declarado ? String(avance) : ''}
            disabled={disabled}
            onChange={(e) => onDeclarar?.(e.target.value === '' ? null : Number(e.target.value))}
            data-testid={`declarar-avance-${task?.task_id}`}
            title="Cuánto de esta tarea está hecho. Es otra cosa que las horas gastadas."
          >
            {/* Un guion es honesto: «nadie lo declaró» no es «no avanzó». */}
            <option value="">— sin declarar —</option>
            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((v) => (
              <option key={v} value={v}>{v} %</option>
            ))}
          </select>
        </label>
      )}

      <span
        className={`text-[11px] ${alerta ? 'text-brand-danger font-medium' : 'text-zinc-500'}`}
        title="Horas imputadas contra las estimadas. Es el consumo, no el avance."
      >
        gastado {consumoTexto}
      </span>

      {/* La alerta que llega a tiempo. Cuando la fecha se vence, ya es tarde. */}
      {alerta && (
        <span
          className="inline-flex items-center gap-1 text-[10px] text-brand-danger bg-red-50 border border-red-200 rounded-sm px-1 py-0.5"
          data-testid={`desvio-${task?.task_id}`}
          title="Se gastó más presupuesto del que se avanzó. Si la tarea estaba mal estimada, quien maneja el plan puede re-estimarla con un motivo."
        >
          <AlertTriangle className="w-2.5 h-2.5 shrink-0" />
          gastaste más de lo que avanzaste
        </span>
      )}
    </div>
  );
}
